import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Box, InputAdornment, IconButton, TextField, Menu, MenuItem, Typography } from "@mui/material";
import {
    Print as PrintIcon, Edit as EditIcon, Delete as DeleteIcon, GetApp as DownloadIcon, FileCopy as CopyIcon, PictureAsPdf as PdfIcon, TableView as ExcelIcon, Search as SearchIcon, Settings as SettingsIcon,
    ArrowBackIosRounded as ArrowBackIosRoundedIcon,
    ArrowForwardIosRounded as ArrowForwardIosRoundedIcon,
    Refresh as RefreshIcon
} from "@mui/icons-material";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

const BOMTableTemplate = ({
    columns,
    headers,
    iterate,
    data,
    onDelete,
    onSearch,
    onEdit = () => { },
    onAdd = () => { },
    onViewChemicals = () => { }, // Add this line
    add = false,
    edit = false,
    recordsPerPage = 9
}) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [filteredData, setFilteredData] = useState(data);

    // Filter data based on search query
    // const filteredData = data.filter(item => {
    //     return Object.values(item).some(value =>
    //         String(value).toLowerCase().includes(searchQuery.toLowerCase())
    //     );
    // });

    const totalPages = Math.ceil(filteredData.length / recordsPerPage);
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    const currentRecords = filteredData.slice(startIndex, endIndex);

    const user = useSelector((state) => state.user);
    // const isAdmin = user?.role === 'Admin'; // Determine if the user is an Admin
    const [isAdmin, setIsAdmin] = useState(() => {
        if (!user || !user.department) return false; // Handle case where user or department is undefined
        const role = user.role?.toLowerCase();
        const department = user.department?.toLowerCase();
        // Check if user is Admin and department does not include Store or Supply or Others
        const isAdminRole = role === 'admin' && !department.includes('store') && !department.includes('supply') && !department.includes('others');
        
        // Check if user is Super Admin
        const isSuperAdminRole = role === 'super admin';
        return isAdminRole || isSuperAdminRole;
        });

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!location.key || location.key === 'default') {
        navigate("/");
        }
    }, [location, navigate]);

     useEffect(() => {
           console.log("Data updated:", data); // Check if data is being set correctly
           setFilteredData(data.filter(item => {
               return Object.values(item).some(value =>
                   String(value).toLowerCase().includes(searchQuery.toLowerCase())
               );
           }));
           setCurrentPage(1); // Reset to first page on search
       }, [searchQuery, data]);

    const handleSearch = () => {
        onSearch(searchQuery);
    };

    // const handleSearchKeyDown = (event) => {
    //     if (event.key === "Enter") {
    //         handleSearch();
    //     }
    // };

       const handleSearchKeyDown = (event) => {
       if (event.key === "Enter") {
           // You can call setFilteredData here if you want to trigger filtering
           // But if you don't need onSearch, you can just leave it empty
           setFilteredData(data.filter(item => {
               return Object.values(item).some(value =>
                   String(value).toLowerCase().includes(searchQuery.toLowerCase())
               );
           }));
           setCurrentPage(1); // Reset to first page on search
       }
   };

    const handlePrevPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handleMenuOpen = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handlePrint = () => {
        const printableContent = `
            <html>
                <head>
                    <title>Print Table</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                        th, td { padding: 8px; border: 1px solid #ddd; text-align: left; }
                        th { background-color: #f4f4f4; font-weight: bold; }
                    </style>
                </head>
                <body>
                    <h1>Table Data</h1>
                    <table>
                        <thead>
                            <tr>${headers.columns.map(col => `<th>${col}</th>`).join('')}</tr>
                        </thead>
                        <tbody>
                            ${iterate.map(entry => `
                                <tr>
                                    ${columns.map(value => `<td>${entry[value]}</td>`).join('')}
                                </tr>`).join('')}
                        </tbody>
                    </table>
                </body>
            </html>
        `;

        const printWindow = window.open("", "", "width=900,height=650");
        printWindow.document.write(printableContent);
        printWindow.document.close();
        printWindow.print();
    };

    const handleExportCSV = () => {
        const csvContent = iterate.map(entry =>
            columns.map(value => `${entry[value]}`).join(',')).join("\n");
        const csvHeader = headers.columns.join(',');
        const csvData = [csvHeader, csvContent].join("\n");
        const blob = new Blob([csvData], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "data.csv";
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleSavePDF = () => {
        const doc = new jsPDF();
        doc.text("Table Data", 10, 10);
        const tableData = iterate.map(entry => columns.map(value => entry[value]));
        autoTable(doc, {
            head: [headers.columns],
            body: tableData
        });
        doc.save("data.pdf");
    };

    const handleExportExcel = () => {
        const customHeaders = headers.columns.filter(header => header.toLowerCase() !== "id");
        const data = iterate.map(entry => columns.map(value => entry[value]));
        const worksheetData = [customHeaders, ...data];
        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Table Data");
        XLSX.writeFile(workbook, "data.xlsx");
    };

    const handleCopyAsText = () => {
        const header = headers.columns.join(',');
        const textContent = iterate.map(entry =>
            columns.map(value => `${entry[value]}`).join(",")).join("\n");
        const fullContent = `${header}\n${textContent}`;

        navigator.clipboard.writeText(fullContent)
            .then(() => alert("Copied to clipboard!"))
            .catch((err) => alert("Failed to copy: " + err));
    };

    const handleRefresh = () => {
       setSearchQuery("");
       setFilteredData(data); // Reset filtered data to original data
       setCurrentPage(1);
   };

    return (
        <>
            <Box display="flex" alignItems="center" gap="10px" marginBottom="10px" marginLeft="10px">
                            {add && isAdmin && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={onAdd}
                                    sx={{
                                        backgroundColor: '#00308F',
                                        color: '#ffffff',
                                        padding: '3px 5px',
                                        '&:hover': {
                                            backgroundColor: '#00207E',
                                        },
                                    }}
                                >
                                    New
                                </Button>
                            )}
                            <Typography variant="h5">
                                {headers.title}
                            </Typography>
                            {/* <IconButton onClick={handleMenuOpen}>
                                <SettingsIcon />
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>
                                    <PrintIcon style={{ marginRight: 8 }} /> Print
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportCSV(); handleMenuClose(); }}>
                                    <DownloadIcon style={{ marginRight: 8 }} /> Export CSV
                                </MenuItem>
                                <MenuItem onClick={() => { handleSavePDF(); handleMenuClose(); }}>
                                    <PdfIcon style={{ marginRight: 8 }} /> Save as PDF
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportExcel(); handleMenuClose(); }}>
                                    <ExcelIcon style={{ marginRight: 8 }} /> Export Excel
                                </MenuItem>
                                <MenuItem onClick={() => { handleCopyAsText(); handleMenuClose(); }}>
                                    <CopyIcon style={{ marginRight: 8 }} /> Copy as Text
                                </MenuItem>
                            </Menu> */}
                            {/* <Box flexGrow={1} display="flex" justifyContent="center">
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={handleSearchKeyDown}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton onClick={handleSearch}>
                                                    <SearchIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    style={{ width: "40%" }}
                                />
                            </Box> */}
                            <Box flexGrow={1} display="flex" justifyContent="center">
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => {
                                        console.log("Search query:", e.target.value); // Log the search query
                                        setSearchQuery(e.target.value);
                                    }}
                                    onKeyDown={handleSearchKeyDown}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                            <IconButton>
                                                <SearchIcon />
                                            </IconButton>
                                            </InputAdornment>
                                         ),
                                    }}
                                    style={{ width: "100%" }}
                                    sx={{ 
                                        backgroundColor: '#ffffff',
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': {
                                            borderColor: '#00308F',
                                            },
                                            '&:hover fieldset': {
                                            borderColor: '#00207E',
                                            },
                                        },
                                    }}
                                />
                            </Box>
                            <Box display="flex" alignItems="center" gap="5px" style={{ marginRight: "10px" }}>
                                <Typography variant="body">
                                    {`${startIndex + 1}-${Math.min(endIndex, data.length)} / ${data.length}`}
                                </Typography>
                                <IconButton
                                    onClick={handlePrevPage}
                                    disabled={currentPage === 1}
                                    style={{
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        width: "36px",
                                        height: "36px",
                                        backgroundColor: "#f9f9f9"
                                    }}
                                >
                                    <ArrowBackIosRoundedIcon />
                                </IconButton>
                                <IconButton
                                    onClick={handleNextPage}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        border: "1px solid #ccc",
                                        borderRadius: "4px",
                                        width: "36px",
                                        height: "36px",
                                        backgroundColor: "#f9f9f9"
                                    }}
                                >
                                    <ArrowForwardIosRoundedIcon />
                                </IconButton>
            
                                <IconButton onClick={handleMenuOpen}>
                                <SettingsIcon />
                            </IconButton>
                            <Menu
                                anchorEl={menuAnchor}
                                open={Boolean(menuAnchor)}
                                onClose={handleMenuClose}
                            >
                                <MenuItem onClick={() => { handlePrint(); handleMenuClose(); }}>
                                    <PrintIcon style={{ marginRight: 8 }} /> Print
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportCSV(); handleMenuClose(); }}>
                                    <DownloadIcon style={{ marginRight: 8 }} /> Export CSV
                                </MenuItem>
                                <MenuItem onClick={() => { handleSavePDF(); handleMenuClose(); }}>
                                    <PdfIcon style={{ marginRight: 8 }} /> Save as PDF
                                </MenuItem>
                                <MenuItem onClick={() => { handleExportExcel(); handleMenuClose(); }}>
                                    <ExcelIcon style={{ marginRight: 8 }} /> Export Excel
                                </MenuItem>
                                <MenuItem onClick={() => { handleCopyAsText(); handleMenuClose(); }}>
                                    <CopyIcon style={{ marginRight: 8 }} /> Copy as Text
                                </MenuItem>
                            </Menu>
                            <IconButton onClick={handleRefresh}>
                                <RefreshIcon />
                            </IconButton>
                            </Box>
                        </Box>
                        <Box
                            style={{
                                flex: 1,
                                overflowY: "auto",
                            }}
                        >
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead style={{ position: "sticky", top: 0, backgroundColor: "#f9f9f9", zIndex: 1 }}>
                        <tr>
                            {headers.columns.map((header, index) => (
                                <th
                                    key={index}
                                    style={{ padding: "10px", textAlign: "center", borderTop: "1px solid #ddd", borderBottom: "1px solid #ddd" }}
                                >
                                    {header}
                                </th>
                            ))}
                            <th style={{ padding: "10px", textAlign: "center", borderTop: "1px solid #ddd", borderBottom: "1px solid #ddd" }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentRecords.map((entry, index) => (
                            <tr
                                key={entry.bom_id}
                                style={{
                                    backgroundColor: index % 2 === 0 ? "#fff" : "#f9f9f9",
                                    borderBottom: "1px solid #ddd",
                                }}
                            >
                                {columns.map((column, idx) => (
                                    <td key={idx} style={{ padding: "10px", textAlign: "center", border: "none" }}>
                                        {entry[column]}
                                    </td>
                                ))}
                                <td style={{ padding: "10px", textAlign: "center", border: "none" }}>
                                <IconButton color="secondary" onClick={() => onViewChemicals(entry.bom_id, entry.part_id)}>
                                        {/* Assuming part_id is available in the entry */}
                                        <Typography variant="body2">View Chemicals</Typography>
                                    </IconButton>
                                    {edit && isAdmin && ( <IconButton color="primary" onClick={() => onEdit(entry)}>
                                        <EditIcon />
                                    </IconButton>)}
                                    {isAdmin && (<IconButton color="secondary" onClick={() => onDelete(entry.bom_id)}>
                                        <DeleteIcon />
                                    </IconButton>)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </>
    );
};

export default BOMTableTemplate;